import React from "react";

interface Column<T> {
  key: keyof T | string;
  header: React.ReactNode;
  render?: (row: T) => React.ReactNode;
  className?: string;
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (row: T) => string | number;
  emptyState?: React.ReactNode;
  className?: string;
}

export function Table<T>({ data, columns, keyExtractor, emptyState, className = "" }: TableProps<T>) {
  return (
    <div className={`w-full overflow-x-auto rounded-md border border-border ${className}`}>
      <table className="w-full text-sm font-sans border-collapse">
        <thead>
          <tr className="border-b border-border bg-surface-2/40">
            {columns.map((col, i) => (
              <th
                key={i}
                className={`px-3.5 py-2.5 text-left text-[10px] tracking-[2px] text-tertiary uppercase font-medium ${col.className ?? ""}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-secondary italic text-sm">
                {emptyState ?? "Aucune donnée"}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={keyExtractor(row)}
                className="border-b border-foreground/[0.04] last:border-0 hover:bg-foreground/[0.015] transition-colors"
              >
                {columns.map((col, i) => (
                  <td key={i} className={`px-3.5 py-2.5 text-foreground/85 ${col.className ?? ""}`}>
                    {col.render
                      ? col.render(row)
                      : String((row as Record<string, unknown>)[col.key as string] ?? "")}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
